import React, {PureComponent} from 'react'
import injectSheet from 'react-jss'
import cn from "classnames";
import ImageOrVideo from "../Widgets/ImageOrVideo"
import Caption from "../Widgets/Caption"
import RichTextContainer from "../Widgets/RichTextContainer"
import ShopTheLookCarousel from '../ShopThLook/ShopTheLookCarousel'
import map from 'lodash/map'
import CentredContentContainer from "../Widgets/CentredContentContainer"
import Heading from "../Widgets/Heading"

const MAX_IMAGE_WIDTH = 420;
const DEFAULT_TITLE_TAG = 'h2';
const IMAGE_ABOVE_LAYOUT = 'Above';
const IMAGE_RIGHT_LAYOUT = 'Right';
const MOBILE_GRID_AREAS = '"title" "image" "content"';
const MOBILE_GRID_COLUMNS = 'minmax(0, 1fr)';
const LEFT_GRID_AREAS = '"image title" "image content"';
const RIGHT_GRID_AREAS = '"title image" "content image"';
const TABLET_GRID_COLUMNS = 'minmax(0, 1fr) minmax(0, 1fr)';
const isImageAboveLayout = imageLocation => imageLocation === IMAGE_ABOVE_LAYOUT;
const isImageRightLayout = imageLocation => imageLocation === IMAGE_RIGHT_LAYOUT;
const getGridAreas = imageLocation => isImageRightLayout(imageLocation) ? RIGHT_GRID_AREAS : LEFT_GRID_AREAS;

class ShopTheLookSlice extends PureComponent {

    render() {
        const {classes, item} = this.props;
        const {image, image_size, video, title, title_tag, copy, caption, shop_the_look_title, shopItems, image_location} = item;
        const titleTag = title_tag || DEFAULT_TITLE_TAG;

        const id = map(shopItems, x => x.sku).join(',');

        const renderTitle = (titleClass = classes.title) => {
          return title && <Heading tag={titleTag} className={titleClass}>{title.text}</Heading>
        }

        const renderCopy = () => {
          return copy && <RichTextContainer className={classes.body} content={copy} removeBottomMargin={false} />
        }

        const renderShopItems = ({ size } = {}) => {
          return shopItems ? (
            <ShopTheLookCarousel
              className={classes.productsContainer}
              shopItems={shopItems}
              id={id}
              groupCells={true}
              showNextPrevButtons={shopItems.length <= 5 ? false : true}
              overflow={false}
              size={size}
              title={shop_the_look_title}
              isMobile={true}
            />
            ) : null
        }

        const renderImage = ({ aspect } = {}) => {
          return (
            <div className={classes.imageContainer}>
                { ((image && image.desktop) || video) &&
                <div className={classes.image}>
                    <ImageOrVideo images={image}
                                  video={video}
                                  aspect={aspect}
                                  sizes={`min-width: ${image_size || MAX_IMAGE_WIDTH}px ${image_size || MAX_IMAGE_WIDTH}px, 100vw`}
                                  caption={caption}>
                    </ImageOrVideo>
                    {caption && <Caption key="caption" caption={caption}></Caption>}
                </div>
                }
            </div>
          )
        }

        if(isImageAboveLayout(image_location)) {
          return (
              <CentredContentContainer className={classes.imageAboveContainer}>
                <section className={classes.imageAbove}>
                  <div className={cn(classes.verticalContainer, classes.dynamicWidth)} >
                    {renderTitle()}
                    {renderCopy()}
                    {renderImage()}
                    {renderShopItems({ size: 'medium' })}
                  </div>
                </section>
              </CentredContentContainer>
          )
        }

        return (
          <CentredContentContainer>
            <section>
              <div className={classes.container} >
                {renderTitle(cn(classes.title, classes.containerTitle))}
                <div className={cn(classes.column, classes.imageColumn)}>
                  {renderImage()}
                </div>
                <div className={cn(classes.column, classes.contentColumn)}>
                  <div className={classes.contentContainer}>
                    {renderCopy()}
                    {renderShopItems({ size: 'medium' })}
                  </div>
                </div>
              </div>
            </section>
          </CentredContentContainer>
        )
    }
}

export default injectSheet((theme) => ({
  verticalContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: MAX_IMAGE_WIDTH * 2,
    margin: 'auto',
    '& $imageContainer': {
      margin: [0, 'auto', '2rem'],
      maxWidth: 630,
      width: '100%'
    },
  },
  container: {
      display: "grid",
      gridTemplateColumns: MOBILE_GRID_COLUMNS,
      gridTemplateAreas: MOBILE_GRID_AREAS,
      [theme.breakpoint("tablet")]: {
          gridTemplateColumns: TABLET_GRID_COLUMNS,
          gridTemplateRows: "auto 1fr",
          gridTemplateAreas: ({item: {image_location}}) => getGridAreas(image_location),
      },
  },
  column: {
      flexBasis: "50%"
  },
  imageColumn: {
      gridArea: "image",
      zIndex: theme.zIndex.carousel + 1
  },
  contentColumn: {
      gridArea: "content"
  },
  contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: MAX_IMAGE_WIDTH,
      margin: [0, 'auto'],
      height: '100%',
      [theme.breakpoint("tablet")]: {
          margin: ({item: {image_location}}) => isImageRightLayout(image_location) ? '0 0 0 auto' : '0 auto 0 0'
      }
  },
  imageContainer: {
      maxWidth: MAX_IMAGE_WIDTH,
      margin: [0, 'auto', theme.spacing.small],
      [theme.breakpoint("tablet")]: {
          margin: ({item: {image_location}}) => isImageRightLayout(image_location) ? '0 auto 0 30px' : '0 30px 0 auto'
      }
  },
  title: {
      fontWeight: "normal",
      fontFamily: theme.font.titleFontFamily,
      fontSize: theme.getRemValue(16),
      textTransform: "uppercase",
      lineHeight: 1.5,
      marginBottom: '1rem',
      [theme.breakpoint("mobile")]: {
          fontSize: theme.getRemValue(18),
      },
      [theme.breakpoint("tablet")]: {
          fontSize: theme.getRemValue(23),
      },
  },
  containerTitle: {
      gridArea: "title",
      width: '100%',
      maxWidth: MAX_IMAGE_WIDTH,
      boxSizing: 'border-box',
      marginLeft: 'auto',
      marginRight: 'auto',
      [theme.breakpoint("tablet")]: {
          marginLeft: ({item: {image_location}}) => isImageRightLayout(image_location) ? 'auto' : 0,
          marginRight: ({item: {image_location}}) => isImageRightLayout(image_location) ? 0 : 'auto',
      },
  },
  body: {
      flex: 1
  },
  imageAboveContainer: {
    maxWidth: '100% !important',
    padding: [0, theme.spacing.tiny],
    [theme.breakpoint('mobile')]: {
      padding: 0
    },
  },
  imageAbove: {
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: 0,
    [theme.breakpoint('tablet')]: {
        maxWidth: '100%'
    },
    [theme.breakpoint('laptop')]: {
        padding: [0, theme.spacing.tiny],
        maxWidth: ({item: {image_size}}) => image_size ? image_size : '100%'
    },
    '& *': {
      maxWidth: '100% !important'
    }
  }
}))(ShopTheLookSlice)



// WEBPACK FOOTER //
// ./src/components/Article/ShopTheLookSlice.js